import { inject, Injectable } from "@angular/core";

import {
    LiquidViewerAnnotation,
    LiquidViewerAnnotationType,
    LiquidViewerIndex,
    LiquidViewerIndexItem,
    LiquidViewerMainContent,
    DatabaseAnnotation,
    DatabaseAnnotationData,
    LiquidViewerAnnotationData,
    DatabaseAnnotationType,
} from "../liquid-viewer.models";
import {
    CoursesService,
} from "src/index/services.index";
import {
    LiquidPageContent
} from "../liquid-page/liquid-page.models";

@Injectable({
    providedIn: 'root'
})
export class LiquidViewerConverterService {

    courseService = inject(CoursesService)

    /* --------------------------- Conversione indice ------------------------------- */
    convertIndex(mainId: string, data: any): LiquidViewerIndex {
        const convertedItems: LiquidViewerIndexItem[] = [];

        if (!data.modules) {
            return { id: mainId, items: convertedItems };
        }

        const converted = this.courseService.convertPackageCourse(data, true);
        const originalModules = converted.modules;

        if (!originalModules) {
            return { id: mainId, items: convertedItems };
        }

        originalModules.forEach((mod: any) => {
            const moduleIndex = mod.index ? mod.index : undefined;
            if (moduleIndex.length > 0) {

                let moduleChildren: LiquidViewerIndexItem[] = [];

                // Scorro il campo "index" del modulo
                // Per ogni elemento
                // - se type === 'chapter" => nuovo capitolo
                // - se type !== 'chapter" => nuova "page"
                //    - se "chapterUuid" -> figlia di un capitolo
                //    - altrimenti -> figlia del modulo
                moduleIndex.forEach((moduleIndexItem: any) => {
                    const chapterChildren = moduleIndex
                        .filter((item: any) => item.type !== 'chapter' && item.uuid !== moduleIndexItem.uuid && item.chapterUuid === moduleIndexItem.uuid);

                    if (chapterChildren.length > 0) {
                        chapterChildren.forEach((chapC: any) => {
                            chapC.children = moduleIndex
                                .filter((item: any) => item.type !== 'chapter' && item.chapterUuid === chapC.uuid);

                            if (chapC.children.length > 0) {
                                chapC.children = chapC.children.map((item: any) => {
                                    return {
                                        id: `${item.uuid}`,
                                        type: 'page',
                                        title: item.title,
                                        num: item.number,
                                        liquidType: item.type,
                                        mainId: mainId,
                                        secondaryMainId: mod.id,
                                        optional: item.optional ?? false,
                                    } as LiquidViewerIndexItem
                                })
                            }
                        });

                        if (moduleIndexItem.type === 'chapter') {
                            moduleChildren.push({
                                id: `${mod.id}`,
                                mainId: mainId,
                                type: 'chapter',
                                title: moduleIndexItem.title,
                                num: moduleIndexItem.number,
                                children: chapterChildren.map((item: any) => {
                                    return {
                                        id: `${item.uuid}`,
                                        type: 'page',
                                        title: item.title,
                                        num: item.number,
                                        liquidType: item.type,
                                        mainId: mainId,
                                        secondaryMainId: mod.id,
                                        children: item.subpages,
                                        optional: item.optional ?? false,
                                    } as LiquidViewerIndexItem
                                }),
                            });
                        } else if (!moduleIndexItem.chapterUuid) {
                            moduleChildren.push({
                                id: `${moduleIndexItem.uuid}`,
                                mainId: mainId,
                                secondaryMainId: mod.id,
                                type: 'page',
                                liquidType: moduleIndexItem.type,
                                title: moduleIndexItem.title,
                                num: moduleIndexItem.number,
                                optional: moduleIndexItem.optional ?? false,
                                children: chapterChildren.map((item: any) => {
                                    return {
                                        id: `${item.uuid}`,
                                        type: 'page',
                                        liquidType: item.type,
                                        title: item.title,
                                        num: item.number,
                                        mainId: mainId,
                                        secondaryMainId: mod.id,
                                        children: item.subpages,
                                        optional: item.optional ?? false,
                                    } as LiquidViewerIndexItem
                                }),
                            });
                        }
                    } else if (!moduleIndexItem.chapterUuid) {
                        moduleChildren.push({
                            id: `${moduleIndexItem.uuid}`,
                            mainId: mainId,
                            secondaryMainId: mod.id,
                            type: 'page',
                            liquidType: moduleIndexItem.type,
                            title: moduleIndexItem.title,
                            num: moduleIndexItem.number,
                            optional: moduleIndexItem.optional ?? false,
                        } as LiquidViewerIndexItem);
                    }
                });

                // Ogni modulo -> un nuovo chapter
                convertedItems.push({
                    id: `${mod.id}`,
                    type: 'chapter',
                    title: mod.title,
                    num: mod.number,
                    children: moduleChildren
                } as LiquidViewerIndexItem);
            }
        });

        return { id: mainId, items: convertedItems };
    }

    /* --------------------------- Conversione contenuto principale ------------------------------- */
    convertMainContent(mainContent: any): LiquidViewerMainContent {
        mainContent = mainContent.result ? mainContent.result : mainContent;
        return {
            id: mainContent.id || mainContent.uuid,
            title: mainContent.title,
            cover: mainContent.image || mainContent.cover,
            description: mainContent.abstract || mainContent.description,
            index: this.convertIndex(mainContent.id || mainContent.uuid, mainContent),
            annotations: [],
        }
    }

    /* --------------------------- Conversione contenuto pagina ------------------------------- */
    convertPageContent(pageContent: any): LiquidPageContent { // TODO: tipizzare pageContent
        if (pageContent.result) {
            pageContent = pageContent.result;
        }

        let pageStructure = pageContent.template;
        let pageElements = pageContent.contents || [];
        if (pageStructure) {
            const templateElement = document.createElement('template');
            const cleanedHtml = pageContent.template.trim().replace(/\n/g, '').replace(/>\s+</g, '><');
            templateElement.innerHTML = cleanedHtml;
            const container = templateElement.content.querySelector('#container') as HTMLElement;
            const elements = container.querySelectorAll('element');
            if (elements.length !== Object.values(pageElements).length) {
                console.error('[Liquid Viewer Converter] Numero di tag <element> in "template" diverso dal numero di item in "contents"');
            }
            const h1 = container.querySelectorAll('h1');
            if (h1.length > 1) {
                console.error('[Liquid Viewer Converter] Più di un tag <h1> in pagina');
            }
            pageStructure = container.innerHTML;
        } else if (pageContent.file) {
            // Per ora si gestisce in questo modo:
            // - valore campo "file", prendo l'estensione -> se .pdf => PDF
            // TODO: Verificare sulle altre tipologie esistenti!
            const result = this.createStructureAndElement(pageContent);
            pageStructure = result.template;
            pageElements = result.contents;
        }

        return {
            structure: pageStructure,
            elements: pageElements,
        }
    }

    createStructureAndElement(pageContent: any) { // TODO: tipizzare pageContent
        const newContentId = 'content-' + parseInt(`${Math.random() * 10000}`);
        const lastDotIndex = pageContent.file.lastIndexOf('.');
        let fileExt;
        if (lastDotIndex !== -1) {
            fileExt = pageContent.file.substring(lastDotIndex + 1, pageContent.file.length);
        }

        const newContentsItem = {
            id: newContentId,
            type: '',
            value: '',
        }

        switch (fileExt) {
            case 'pdf':
                newContentsItem.type = 'pdf';
                newContentsItem.value = `${pageContent.file}`;
                break;
        }

        const newContents: { [key: string]: any } = {};
        newContents[newContentId] = newContentsItem;

        return {
            template: `<element id="${newContentId}"></element>`,
            contents: newContents,
        }
    }

    /* --------------------------- Conversione annotazioni ------------------------------- */
    convertToDataseAnnotation(annotation: LiquidViewerAnnotation): DatabaseAnnotation | undefined {
        return {
            userId: annotation.userId!,
            uuid: annotation?.id,
            volumeId: annotation.mainId,
            coursePublicationUuid: annotation.mainId,
            moduleId: annotation.secondaryMainId,
            moduleUuid: annotation.secondaryMainId,
            contentId: annotation.contentId,
            title: annotation.title,
            type: this.convertToDatabaseAnnotationType(annotation.type),
            data: this.convertToDatabaseAnnotationData(annotation.data),
        }
    }

    convertToDatabaseAnnotationData(data: Partial<LiquidViewerAnnotationData> | undefined): Partial<DatabaseAnnotationData> | undefined {
        if (data) {
            return {
                ...data.legacyData,
                clippingUrl: data.clippingSrc,
                quote: data.text,
                color: data.color,
                text: data.textNote,
            } 
        } 
        return data;
    }

    convertToDatabaseAnnotationType(type: LiquidViewerAnnotationType): DatabaseAnnotationType {
        switch (type) {
            case 'link':
            case 'preferences':
                return 'multimedia';
            default:
                return type;
        }
    }

    convertAnnotations(data: DatabaseAnnotation[]): LiquidViewerAnnotation[] {
        if (data && Array.isArray(data)) {
            return data.map(item => ({
                id: item.uuid,
                userId: item.userId,
                mainId: (item.volumeId || item.coursePublicationUuid)!,
                secondaryMainId: item.moduleId || item.moduleUuid,
                contentId: item.contentId,
                title: item.title,
                data: this.remapAnnotationData(item.type, item.data),
                type: this.remapAnnotationType(item.type),
                icon: this.getAnnotationIconByType(item.type),
                messageDeletePopup: this.getDeleteMessageByAnnotationType(item.type),
                createdAt: item.createdAt,
            }))
        }
        return [];
    }

    getAnnotationIconByType(type: LiquidViewerAnnotationType | DatabaseAnnotationType): string {
        switch (type) {
            case 'highlight':
                return 'highlight';
            case 'link':
            case 'multimedia':
                return 'link';
            case 'clipping':
                return 'crop';
            case 'bookmark':
                return 'star-fill';
            default:
                return 'file';
        }
    }

    getDeleteMessageByAnnotationType(type: LiquidViewerAnnotationType | DatabaseAnnotationType): string {
        const startText = "Are you sure you want to remove"
        switch (type) {
            case 'highlight':
                return `${startText} the highlight?`;
            case 'clipping':
                return `${startText} the clipping?`;
            case 'bookmark':
                return `${startText} the bookmark?`;
            case 'link':
            case 'multimedia':
                return `${startText} the link?`;
            default:
                return `${startText} the note?`;
        }
    }

    remapAnnotationData(type: DatabaseAnnotationType, data: Partial<DatabaseAnnotationData> | undefined) {
        switch (type) {
            case 'highlight':
            case 'note':
            case 'multimedia':
                return {
                    text: data?.quote || data?.text,
                    textNote: data?.text,
                    color: data?.color || '',
                    legacyData: data,
                };
            case 'clipping':
                return {
                    clippingSrc: data?.clippingUrl,
                    legacyData: data,
                }
            default:
                return {
                    ...data,
                    legacyData: data,
                };
        }
    }

    remapAnnotationType(type: DatabaseAnnotationType): LiquidViewerAnnotationType {
        switch (type) {
            case 'multimedia':
                return 'link';
            default:
                return type;
        }
    }
}